import { useState, useEffect, useRef, useMemo } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { getEnabledProducts } from '../data/products'
import { getEnabledNavPages, isPageEnabled } from '../config/siteConfig'
import './Header.css'

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const searchRef = useRef(null)
  const navigate = useNavigate()

  // Get enabled products for search
  const enabledProducts = useMemo(() => getEnabledProducts(), [])
  
  // Get enabled navigation pages
  const navPages = useMemo(() => getEnabledNavPages(), [])

  // Filter products based on search query
  const searchSuggestions = useMemo(() => {
    if (!searchQuery.trim()) return []
    const query = searchQuery.toLowerCase()
    return enabledProducts.filter(p => 
      p.name.toLowerCase().includes(query) ||
      p.tagline.toLowerCase().includes(query)
    ).slice(0, 5) // Limit to 5 suggestions
  }, [searchQuery, enabledProducts])

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false)
  }, [navigate])

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  const handleSearch = (e) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`)
      setSearchQuery('')
      setIsSearchOpen(false)
      setShowSuggestions(false)
      setIsMenuOpen(false)
    }
  }

  const handleSearchInput = (e) => {
    setSearchQuery(e.target.value)
    setShowSuggestions(true)
  }

  const handleSuggestionClick = (productId) => {
    navigate(`/products/${productId}`)
    setSearchQuery('')
    setIsSearchOpen(false)
    setShowSuggestions(false)
    setIsMenuOpen(false)
  }

  const closeAllMenus = () => {
    setIsMenuOpen(false)
    setShowSuggestions(false)
  }

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="container">
        <nav className="nav">
          {/* Logo */}
          <Link to="/" className="logo" onClick={closeAllMenus}>
            <img 
              src="/assets/images/logo.png" 
              alt="SIBR Lifescience" 
              className="logo-image"
            />
            <div className="logo-text">
              <span className="logo-name">SIBR <span className="logo-name-sub">Lifescience</span></span>
              <span className="logo-byline">Provides Healthy Life & Care with Quality</span>
            </div>
          </Link>

          {/* Navigation Links */}
          <ul className={`nav-links ${isMenuOpen ? 'active' : ''}`}>
            {/* Home link (always shown if enabled) */}
            {isPageEnabled('home') && (
              <li>
                <NavLink to="/" onClick={closeAllMenus} end>
                  Home
                </NavLink>
              </li>
            )}
            
            {/* Dynamic navigation from config */}
            {navPages.map(page => (
              <li key={page.key}>
                <NavLink to={page.path} onClick={closeAllMenus}>
                  {page.title}
                </NavLink>
              </li>
            ))}

            {/* Mobile-only: Search */}
            <li className="mobile-only mobile-search-item">
              <form className="mobile-search-form" onSubmit={handleSearch}>
                <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="M21 21l-4.35-4.35"/>
                </svg>
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={handleSearchInput}
                  autoComplete="off"
                />
              </form>
            </li>

            {/* Mobile-only: 1mg link */}
            <li className="mobile-only">
              <a 
                href="https://www.1mg.com/marketer/sibr-life-sciences-private-limited-84382"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeAllMenus}
                className="mobile-cta"
              >
                Find Products on 1mg
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                  <polyline points="15 3 21 3 21 9"/>
                  <line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
              </a>
            </li>
          </ul>

          {/* Right Section: Search + CTA + Menu Toggle */}
          <div className="nav-actions">
            {/* Search */}
            <div className="search-wrapper" ref={searchRef}>
              <form className="search-form" onSubmit={handleSearch}>
                <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="M21 21l-4.35-4.35"/>
                </svg>
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={handleSearchInput}
                  onFocus={() => searchQuery && setShowSuggestions(true)}
                  autoComplete="off"
                />
                {searchQuery && (
                  <button 
                    type="button" 
                    className="search-clear"
                    onClick={() => {
                      setSearchQuery('')
                      setShowSuggestions(false)
                    }}
                    aria-label="Clear search"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 6L6 18M6 6l12 12"/>
                    </svg>
                  </button>
                )}
              </form>
              
              {/* Search Suggestions Dropdown */}
              {showSuggestions && searchQuery.trim() && (
                <div className="search-suggestions">
                  {searchSuggestions.length > 0 ? (
                    searchSuggestions.map((product) => (
                      <button
                        key={product.id}
                        className="suggestion-item"
                        onClick={() => handleSuggestionClick(product.id)}
                      >
                        <div className="suggestion-image">
                          {product.image ? (
                            <img src={product.image} alt={product.name} />
                          ) : (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                              <path d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"/>
                            </svg>
                          )}
                        </div>
                        <div className="suggestion-info">
                          <span className="suggestion-name">{product.name}</span>
                          <span className="suggestion-tagline">{product.tagline}</span>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="no-results">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                      </svg>
                      <span>No products found for "{searchQuery}"</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* CTA Button - Desktop only */}
            <a 
              href="https://www.1mg.com/marketer/sibr-life-sciences-private-limited-84382"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary header-cta"
            >
              <span className="cta-text">Buy on 1mg</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                <polyline points="15 3 21 3 21 9"/>
                <line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
            </a>

            {/* Mobile Menu Toggle - Now in nav-actions for right positioning */}
            <button 
              className={`menu-toggle ${isMenuOpen ? 'active' : ''}`}
              onClick={toggleMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </nav>
      </div>
    </header>
  )
}

export default Header
